import { HashRouter, Route, Routes } from "react-router-dom";
import Navbar from "./Layouts/Navbar";
import DashboardLayout from "./Layouts/DashboardLayout";
import Home from "./Pages/Home";
import { Toaster } from "react-hot-toast";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useEffect } from "react";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import TermsOfService from "./Pages/TermsOfService";
import Documentation from "./Pages/Documentation";
import Login from "./Components/Login";
import Signup from "./Components/Signup";
import ForgotPassword from "./Components/ForgotPassword";
import ResetPassword from "./Components/ResetPassword";
import WelcomeTerms from "./Components/WelcomeTerms";
import Dashboard from "./Components/Dashboard";
import Nodes from "./Components/Nodes";
import Analytics from "./Components/Analytics";
import Settings from "./Components/Settings";
import MCPIntegration from "./Components/MCPIntegration";
import Verify from "./Components/Verify";
import AcceptInvite from "./Pages/AcceptInvite";
import GoogleAuthCallback from "./Components/GoogleAuthCallback";
import ProtectedRoute from "./Components/ProtectedRoute";
import Pricing from "./Components/Pricing";
import Solana from "./Pages/Solana";
import SolanaGrpc from "./Pages/SolanaGrpc";
import SubscriptionSuccess from "./Components/SubscriptionSuccess";
import SubscriptionCancel from "./Components/SubscriptionCancel";
import { AuthProvider } from "./context/AuthContext";
import { OrganizationProvider } from "./context/OrganizationContext";
import { ProjectProvider } from "./context/ProjectContext";

const TOAST_BASE = {
  className: "font-secondary !shadow-none",
  style: {
    background: "#1B1B1B",
    color: "rgba(255, 255, 255, 0.9)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "2px",
    padding: "12px 16px",
    fontSize: "15px",
    maxWidth: "min(24rem, calc(100vw - 2rem))",
    boxShadow: "none",
  },
};

const firebaseConfig = {
  apiKey: "AIzaSyCrJTk0-eePPJxOogYYjguHikambSKspIw",
  authDomain: "landing-nodefleet.firebaseapp.com",
  projectId: "landing-nodefleet",
  storageBucket: "landing-nodefleet.appspot.com",
  messagingSenderId: "73192320150",
  appId: "1:73192320150:web:b396bd8457ebb9d863e6b7",
  measurementId: "G-EK7E6FH5DX"
};

export let analytics;

if (typeof window !== "undefined") {
  const app = initializeApp(firebaseConfig);
  analytics = getAnalytics(app);
}

function App() {
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, "page_view");
    }
  }, []);

  return (
    <AuthProvider>
      <OrganizationProvider>
        <ProjectProvider>
          <div className="App">
            <HashRouter>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/welcome-terms"
                  element={
                    <ProtectedRoute>
                      <WelcomeTerms />
                    </ProtectedRoute>
                  }
                />
                <Route path="/verify" element={<Verify />} />
                <Route path="/invites/accept/:token" element={<AcceptInvite />} />
                <Route path="/auth/google/complete" element={<GoogleAuthCallback />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                </Route>
                <Route
                  path="/nodes"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Nodes />} />
                </Route>
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Analytics />} />
                </Route>
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Settings />} />
                </Route>
                <Route
                  path="/mcp"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<MCPIntegration />} />
                </Route>
                {/* Solana gRPC add-on — separate from JSON-RPC subscription.
                    Standalone (not wrapped in DashboardLayout) so it can be
                    linked from the public /solana landing page without the
                    dashboard chrome. Auth is enforced by ProtectedRoute. */}
                <Route
                  path="/solana-grpc"
                  element={
                    <ProtectedRoute>
                      <SolanaGrpc />
                    </ProtectedRoute>
                  }
                />
                {/* Subscription Routes */}
                <Route
                  path="/subscription/success"
                  element={
                    <ProtectedRoute>
                      <SubscriptionSuccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription/cancel"
                  element={
                    <ProtectedRoute>
                      <SubscriptionCancel />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navbar />}>
                  <Route index element={<Home />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/solana" element={<Solana />} />
                  <Route path="/docs" element={<Documentation />} />
                </Route>
              </Routes>
            </HashRouter>
            <Toaster
              position="bottom-center"
              reverseOrder={false}
              containerStyle={{ bottom: 24 }}
              toastOptions={{
                ...TOAST_BASE,
                duration: 4000,
                success: {
                  ...TOAST_BASE,
                  style: {
                    ...TOAST_BASE.style,
                    background: "rgba(73, 183, 248, 0.08)",
                    border: "1px solid rgba(73, 183, 248, 0.25)",
                    color: "rgba(255, 255, 255, 0.92)",
                  },
                  iconTheme: {
                    primary: "#49B7F8",
                    secondary: "#1B1B1B",
                  },
                },
                error: {
                  ...TOAST_BASE,
                  style: {
                    ...TOAST_BASE.style,
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.25)",
                    color: "#fca5a5",
                  },
                  iconTheme: {
                    primary: "#f87171",
                    secondary: "#1B1B1B",
                  },
                },
                loading: {
                  ...TOAST_BASE,
                  style: {
                    ...TOAST_BASE.style,
                    border: "1px solid rgba(73, 183, 248, 0.2)",
                  },
                  iconTheme: {
                    primary: "#49B7F8",
                    secondary: "#1B1B1B",
                  },
                },
              }}
            />
          </div>
        </ProjectProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
}

export default App;
